import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserService } from "@/services/users/UserService";

export type AuditPhase = "idle" | "generating" | "generated" | "executing" | "completed" | "failed";

export interface FullAuditProgressState {
  testRunId: number | null;
  phase: AuditPhase;
  agentName: string | null;
  // Generation phase
  totalCategories: number;
  categoriesComplete: number;
  scenariosGenerated: number;
  targetScenarios: number;
  usedCache: boolean;
  // Execution phase
  testsComplete: number;
  totalTests: number;
  // Final
  overallScore: number | null;
  errorMessage: string | null;
}

const initial: FullAuditProgressState = {
  testRunId: null,
  phase: "idle",
  agentName: null,
  totalCategories: 0,
  categoriesComplete: 0,
  scenariosGenerated: 0,
  targetScenarios: 0,
  usedCache: false,
  testsComplete: 0,
  totalTests: 0,
  overallScore: null,
  errorMessage: null,
};

/**
 * Subscribes to all Brand Audit broadcast events for the current user and
 * keeps a single state object updated for the FullAuditSheet to render.
 *
 * Pass `testRunId` to filter events to one specific audit run.
 */
export function useFullAuditProgress(testRunId: number | null) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<FullAuditProgressState>(initial);
  const testRunIdRef = useRef(testRunId);

  useEffect(() => {
    testRunIdRef.current = testRunId;
  }, [testRunId]);

  // Reset when a new run starts
  useEffect(() => {
    if (testRunId !== state.testRunId) {
      setState({ ...initial, testRunId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testRunId]);

  useEffect(() => {
    if (!testRunId) return;

    let isMounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      const { data: { user } } = await UserService.getUser();
      if (!user || !isMounted) return;
      const userId = user.id;

      // Must match the channel name the edge functions broadcast on.
      channel = supabase.channel("brand-tests");

      const matches = (incomingTestRunId: unknown) => Number(incomingTestRunId) === testRunId;

      type GenerationStarted = { testRunId: number; userId: string; agentName?: string; totalCategories?: number; targetScenarios?: number };
      type GenerationProgress = { testRunId: number; userId: string; scenarioCount?: number };
      type GenerationCacheHit = { testRunId: number; userId: string; scenarioCount?: number };
      type GenerationComplete = { testRunId: number; userId?: string; scenarioCount?: number };
      type TestStarted = { testRunId: number; userId: string; agentName?: string; totalTests?: number };
      type TestProgress = { testRunId: number; userId: string; progress?: number; total?: number };
      type TestCompleted = { testRunId: number; userId?: string; overallScore?: number };
      type FailurePayload = { testRunId: number; error?: string };

      channel
        .on("broadcast", { event: "audit-generation-started" }, ({ payload }: { payload: GenerationStarted }) => {
          if (!isMounted || !matches(payload.testRunId) || payload.userId !== userId) return;
          setState((s) => ({
            ...s,
            phase: "generating",
            agentName: payload.agentName ?? s.agentName,
            totalCategories: payload.totalCategories ?? s.totalCategories,
            targetScenarios: payload.targetScenarios ?? s.targetScenarios,
          }));
        })
        .on("broadcast", { event: "audit-generation-progress" }, ({ payload }: { payload: GenerationProgress }) => {
          if (!isMounted || !matches(payload.testRunId) || payload.userId !== userId) return;
          setState((s) => ({
            ...s,
            phase: "generating",
            categoriesComplete: s.categoriesComplete + 1,
            scenariosGenerated: s.scenariosGenerated + (payload.scenarioCount ?? 0),
          }));
        })
        .on("broadcast", { event: "audit-generation-cache-hit" }, ({ payload }: { payload: GenerationCacheHit }) => {
          if (!isMounted || !matches(payload.testRunId) || payload.userId !== userId) return;
          setState((s) => ({
            ...s,
            usedCache: true,
            scenariosGenerated: payload.scenarioCount ?? s.scenariosGenerated,
          }));
        })
        .on("broadcast", { event: "audit-generation-complete" }, ({ payload }: { payload: GenerationComplete }) => {
          if (!isMounted || !matches(payload.testRunId)) return;
          setState((s) => ({
            ...s,
            phase: "generated",
            scenariosGenerated: payload.scenarioCount ?? s.scenariosGenerated,
            totalTests: payload.scenarioCount ?? s.totalTests,
          }));
        })
        .on("broadcast", { event: "test-started" }, ({ payload }: { payload: TestStarted }) => {
          if (!isMounted || !matches(payload.testRunId) || payload.userId !== userId) return;
          setState((s) => ({
            ...s,
            phase: "executing",
            agentName: payload.agentName ?? s.agentName,
            totalTests: payload.totalTests ?? s.totalTests,
          }));
        })
        .on("broadcast", { event: "test-progress" }, ({ payload }: { payload: TestProgress }) => {
          if (!isMounted || !matches(payload.testRunId) || payload.userId !== userId) return;
          setState((s) => ({
            ...s,
            phase: "executing",
            testsComplete: payload.progress ?? s.testsComplete,
            totalTests: payload.total ?? s.totalTests,
          }));
        })
        .on("broadcast", { event: "test-completed" }, ({ payload }: { payload: TestCompleted }) => {
          if (!isMounted || !matches(payload.testRunId)) return;
          setState((s) => ({
            ...s,
            phase: "completed",
            overallScore: typeof payload.overallScore === "number" ? payload.overallScore : s.overallScore,
            testsComplete: s.totalTests || s.testsComplete,
          }));
          queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
          queryClient.invalidateQueries({ queryKey: ["brand-test-run-details", String(testRunId)] });
        })
        .on("broadcast", { event: "test-failed" }, ({ payload }: { payload: FailurePayload }) => {
          if (!isMounted || !matches(payload.testRunId)) return;
          setState((s) => ({ ...s, phase: "failed", errorMessage: payload.error ?? "Audit failed" }));
        })
        .on("broadcast", { event: "audit-failed" }, ({ payload }: { payload: FailurePayload }) => {
          if (!isMounted || !matches(payload.testRunId)) return;
          setState((s) => ({ ...s, phase: "failed", errorMessage: payload.error ?? "Audit failed" }));
        })
        .subscribe();
    };

    setup();

    return () => {
      isMounted = false;
      // Do NOT call channel.unsubscribe() — supabase.channel() returns a shared
      // instance for the same topic name. Unsubscribing here would tear down the
      // channel that useBrandIntegrityNotifications (always mounted) is also using,
      // which is exactly what causes "Unsubscribed from brand-tests channel" when
      // the FullAuditSheet closes. isMounted=false is enough to silence stale events.
    };
  }, [testRunId, queryClient]);

  const generationPercent = useMemo(() => {
    if (state.usedCache) return 100;
    if (state.totalCategories === 0) return 0;
    return Math.round((state.categoriesComplete / state.totalCategories) * 100);
  }, [state.categoriesComplete, state.totalCategories, state.usedCache]);

  const executionPercent = useMemo(() => {
    if (state.totalTests === 0) return 0;
    return Math.round((state.testsComplete / state.totalTests) * 100);
  }, [state.testsComplete, state.totalTests]);

  return { state, generationPercent, executionPercent };
}
